import { Vue, Component, Ref, Prop, Emit, PropSync } from 'vue-property-decorator'
import { initialViewerState, IViewerState, ImageInfo, ToolTipManager, AnnotationManager, FeaturesManager, PanDirection, ViewerUserConfiguration, ConfigurationManager, ContextMenuItem, UnitType, ScaleInfo, ImageType, KernelType, WMTSImageInfo } from './Viewer.models'
import { getWidth as getExtentWidth, Extent, getCenter, getTopLeft } from 'ol/extent'
import Projection from 'ol/proj/Projection'
import ImageLayer from 'ol/layer/Image'
import TileLayer from 'ol/layer/Tile'
import Static from 'ol/source/ImageStatic'
import WMTS from 'ol/source/WMTS'
import WMTSTileGrid from 'ol/tilegrid/WMTS'
import Layer from 'ol/layer/Layer'
import View from 'ol/View'
import OLMap from 'ol/Map'
import { ToolType } from './tools/Tool'
import ToolFactory from './tools/ToolFactory'
import GeoJSON from 'ol/format/GeoJSON'
import Feature, { FeatureLike } from 'ol/Feature'
import Geometry from 'ol/geom/Geometry'
import VectorSource from 'ol/source/Vector'
import VectorLayer from 'ol/layer/Vector'
import Polygon from 'ol/geom/Polygon'
import LineString from 'ol/geom/LineString'
import Style from 'ol/style/Style'
import Stroke from 'ol/style/Stroke'
import Fill from 'ol/style/Fill'
import { Pixel } from 'ol/pixel'
import MapBrowserEvent from 'ol/MapBrowserEvent'
import Event from 'ol/render/Event'
import ModalViewer from '@/modules/viewerModule/viewer/modals/ModalViewer.vue'
import ModalAttachmentFilename from '@/modules/viewerModule/viewer/modals/ModalAttachmentFilename.vue'
import ModalPictureInfo from '@/modules/viewerModule/viewer/modals/pictureinfo/ModalPictureInfo.vue'
import ModalConfigurationViewer from '@/modules/viewerModule/viewer/modals/ModalConfigurationViewer.vue'
import ModalMeasureScale from '@/modules/viewerModule/viewer/modals/ModalMeasureScale.vue'
// @ts-ignore
import ContextMenu from 'ol-contextmenu'
import 'ol/ol.css'
import 'ol-contextmenu/dist/ol-contextmenu.css'
import SampleAttachmentService from '@/api/resources/attachment/sampleAttachmentService'
import BaseLayer from 'ol/layer/Base'
import { ScaleLine } from 'ol/control'
import { State } from 'ol/render'
import { Coordinate } from 'ol/coordinate'
import SimpleGeometry from 'ol/geom/SimpleGeometry'
import { Nullable } from '@/shared/types/app.types'
import { RotateCustom } from '@/modules/viewerModule/viewer/tools/Rotation.tool'
import { OverViewMapCustom } from '@/modules/viewerModule/viewer/controls/overview-map'
const ATTRIBUTIONS = '<a href="http://www.vitro.bio/">Vitro S.A.</a>'

@Component({
    components: {
        ModalViewer, ModalAttachmentFilename, ModalPictureInfo, ModalConfigurationViewer, ModalMeasureScale
    }
})
export default class ViewerComponent extends Vue implements ToolTipManager, AnnotationManager {
    private state: IViewerState;
    @Prop() imageInfo!: ImageInfo;
    @Prop() wmtsImageInfo!: WMTSImageInfo;
    @Prop() userConfiguration!: ViewerUserConfiguration;
    @Prop() featuresManager: FeaturesManager | undefined;
    @Prop() configurationManager: ConfigurationManager | undefined;
    @PropSync('showMap', { type: Boolean }) showMapSynced!: boolean

    @Ref('overviewBox') readonly overviewBox!: HTMLElement
    @Ref('overviewMap') readonly overviewMap!: HTMLElement
    @Ref('scaleline') readonly scaleLine!: HTMLElement
    @Ref('modalViewerWrapper') readonly modalViewerWrapper!: any
    @Ref('modalAttachmentFilename') readonly modalAttachmentFilename!: any
    @Ref('modalPictureInfo') readonly modalPictureInfo!: any
    @Ref('modalConfigurationViewer') readonly modalConfigurationViewer!: any
    @Ref('modalMeasureScaleWrapper') readonly modalMeasureScaleWrapper!: any
    @Ref('rotationBase') readonly rotationBaseElement!: HTMLElement
    @Ref('rotationIndicator') readonly rotationIndicatorElement!: HTMLElement
    @Ref('rotationLabel') readonly rotationLabelElement!: HTMLElement
    @Ref('kernelSelect') readonly kernelSelect!: HTMLElement
    @Ref('mapDom') readonly mapDOM!: HTMLElement

    constructor() {
        super()
        this.state = initialViewerState()
    }

    mounted():void {
        this.initiateMap()
    }

    beforeDestroy() {
        this.unSubscribeEvents()
    }

    public async loadlayers():Promise<void> {
        this.removeAllAnnotations()
        this.unSubscribeEvents()
        if (this.state.map) {
            this.state.vectorLayer?.getSource().clear()
            this.state.map.updateSize()
        }
        if (this.state.imageLayer) {
            this.state.map?.removeLayer(this.state.imageLayer as BaseLayer)
        }
        if (this.state.vectorLayer) {
            this.state.map?.removeLayer(this.state.vectorLayer as BaseLayer)
        }
        if (this.state.tooltipLayer) {
            this.state.map?.removeLayer(this.state.tooltipLayer as BaseLayer)
        }

        this.state.imageInfo = this.imageInfo
        this.state.wmtsImageInfo = this.wmtsImageInfo
        this.selectColor(this.state.userConfiguration.defaultColor)
        this.state.extent = this.getExtent()
        this.state.imageExtent = this.getImageExtent()
        this.state.extentWidth = getExtentWidth(this.state.extent)
        this.state.resolutions = this.getResolutions(this.state.extentWidth)
        this.state.matrixIds = this.getMatrixIds()
        this.state.projection = this.getProjection(this.state.extent)
        this.state.imageLayer = this.getImageLayer(this.state.extent, this.state.projection, this.state.resolutions, this.state.matrixIds)
        this.state.view = this.getView(this.state.extent, this.state.projection)

        if (this.state.map) {
            this.state.map?.setTarget()
        }

        this.state.map = new OLMap({
            target: 'map',
            controls: [new ScaleLine({
                units: 'metric',
                target: this.scaleLine
            }),
            new RotateCustom(this.rotationBaseElement, this.rotationIndicatorElement, this.rotationLabelElement),
            new OverViewMapCustom(this.overviewBox, this.overviewMap, {
                layers: [
                    (this.state.imageInfo?.type !== ImageType.WSI
                        ? new ImageLayer({
                            source: this.state.imageLayer.getSource()
                        }) : new TileLayer({
                            source: this.state.imageLayer.getSource()
                        }))
                ],
                rotateWithView: true,
                collapsed: false,
                collapsible: false
            })
            ],
            view: this.state.view
        })

        this.state.map.addEventListener('moveend', this._onMapMoveEnd)
        await this.loadFeatures()
        this.state.tooltipLayer = this.generateTooltipLayer()
        this.state.vectorSource = this.getAnnotationSourceFromJsonFeatures(this.state.annotations as Feature<Geometry>[])
        this.state.vectorLayer = this.generateAnnotationLayer(this.state.vectorSource)
        this.state.imageLayer.on('prerender', this._onImageLayerPreRender)

        this.state.map?.addLayer(this.state.imageLayer)
        this.state.map?.addLayer(this.state.vectorLayer)
        this.state.map?.addLayer(this.state.tooltipLayer)

        this.showAnnotations()
        this.state.vectorLayer.setVisible(this.state.userConfiguration.showAnnotations)
        const self = this
        this.state.map.once('postrender', (event) => {
            if (self.state.selectedTool) {
                self.changeTool(self.state.selectedTool)
            }
        })
    }

    private _onMapMoveEnd():void {
        this.recalculateZoomLabel()
    }

    private _onImageLayerPreRender(event:Event): void {
        if (event.context) {
            event.context.imageSmoothingEnabled = true
        }
    }

    private unSubscribeEvents(): void {
        this.state.map?.removeEventListener('moveend', this._onMapMoveEnd)
        this.state.imageLayer?.un('prerender', this._onImageLayerPreRender)
    }

    private recalculateZoomLabel(): void {
        var currZoom = this.state.map?.getView().getZoom()
        var currResolution = this.state.map?.getView().getResolutionForZoom(currZoom ?? 0)
        if (currResolution) {
            if (this.state.imageInfo?.type !== ImageType.WSI) {
                const zoomFactor = 1 / currResolution
                this.state.zoomLabel = `${Math.round(zoomFactor * 10) / 10}x`
            } else {
                const zoomFactor = this.state.wmtsImageInfo.objective / currResolution
                this.state.zoomLabel = `${Math.round(zoomFactor * 10) / 10}x`
            }
        }
    }

    public get zoomLabels(): string[] {
        if (this.state.wmtsImageInfo.objective >= 60) {
            return ['1', '4', '10', '20', '40', '60']
        } else if (this.state.wmtsImageInfo.objective >= 40) {
            return ['1', '2', '4', '10', '20', '40']
        } else {
            return ['1', '2', '4', '8', '10', '20']
        }
    }

    public async initiateMap(): Promise<void> {
        ToolFactory.reset()
        let userConfig = await this.configurationManager?.loadConfiguration()
        if (userConfig && userConfig != null) {
            this.state.userConfiguration = userConfig
        }
        await this.loadlayers()

        this.state.map?.on('pointermove', this.pointerMoveHandler)
        this.state.map?.getViewport().addEventListener('mouseout', this.pointerMoveOutHandler)
        this.state.map?.getView()?.setResolution(0.1)

        this.state.contextMenu = new ContextMenu({
            width: 250
        })

        this.state.map?.addControl(this.state.contextMenu)

        this.state.contextMenu.on('beforeopen', (evt: any) => {
        })

        this.state.contextMenu.on('open', (evt: any) => {
            const feature = this.getFeatureAtPosition(evt.pixel)
            if (feature) {
                this.state.contextMenu.clear()
                this.state.contextMenu.extend(this.getFeatureContextMenu(<Feature<Geometry>>feature))
            } else {
                this.state.contextMenu.clear()
                this.state.contextMenu.extend(this.getMapContextMenu())
            }
        })

        this.state.contextMenu.on('close', (evt: any) => {
        })

        this.state.map?.on('click', (evt) => {
        })

        // this.state.view?.setCenter([this.state.imageInfo.width / 2, -this.state.imageInfo.height / 2])
        // this.state.view?.fit(this.getExtent())
        this.state.view?.fit(this.state.extent!, <any>{ size: this.state.map?.getSize() })
    }

    _getLocalizedKernelName(kernel: string): string {
        return this.$tc(`viewer.kernels.${kernel}`)
    }

    public onKernelChange(selectedKernel:string):void {
        this.state.selectedKernel = (<any>KernelType)[selectedKernel.toUpperCase()]
        this.updateImageLayer()
    }

    public onBrightnessChange(event: any):void {
        this.state.selectedBrightness = event.currentTarget.value
        this.updateImageLayer()
    }

    public onContrastChange(event: any):void {
        this.state.selectedContrast = event.currentTarget.value / 100
        this.updateImageLayer()
    }

    private updateImageLayer(): void {
        if (this.state.extent && this.state.projection && this.state.resolutions && this.state.matrixIds && this.state.imageLayer) {
            this.state.imageLayer = this.getImageLayer(this.state.extent, this.state.projection, this.state.resolutions, this.state.matrixIds)
            this.state.map?.getLayers().insertAt(1, this.state.imageLayer)
            this.state.map?.getLayers().removeAt(0)
        }
    }

    public get effectList(): string[] {
        return [KernelType.NONE, KernelType.BLUR, KernelType.EDGE, KernelType.EMBOSS, KernelType.SHADOW, KernelType.SHARPEN, KernelType.SHARPENLESS]
    }

    public get showSaveButton(): boolean {
        return this.imageInfo?.type !== ImageType.WSI
    }

    public get showMeasureButtons(): boolean {
        return this.imageInfo?.type !== ImageType.WSI
    }

    private getExtent(): Extent {
        return this.getImageExtent()
    }

    private getImageExtent(): Extent { // minY = -87974, maxX = 106929): Extent {
        if (this.state.imageInfo?.type !== ImageType.WSI) {
            return [0, 0, this.state.imageInfo.width, this.state.imageInfo.height]
        } else {
            // x1,y1 ------- x2,y1
            //  |              |
            //  |              |
            //  |              |
            //  |              |
            //  |              |
            // x1,y2 ------- x2,y2
            // extent=[x1, y2, x2, y1]
            // let extent:Extent = [0, this.state.imageInfo.height, this.state.imageInfo.width, 0]
            // return [0, this.state.imageInfo.height, this.state.imageInfo.width, 0]

            /*
            * Frames may extend beyond the size of the total pixel matrix.
            * The excess pixels are empty, i.e. have only a padding value.
            * We set the extent to the size of the actual image without taken
            * excess pixels into account.
            * Note that the vertical axis is flipped in the used tile source,
            * i.e. values on the axis lie in the range [-n, -1], where n is the
            * number of rows in the total pixel matrix.
            */
            const baseTotalPixelMatrixRows = this.state.imageInfo.height
            const baseTotalPixelMatrixColumns = this.state.imageInfo.width
            const extent:Extent = [
                0, // min X
                -(baseTotalPixelMatrixRows + 1), // min Y
                baseTotalPixelMatrixColumns, // max X
                0 // max Y
            ]
            return extent
        }
    }

    private getResolutions(size: number): number[] {
        if (this.state.imageInfo?.type !== ImageType.WSI) {
            return []
        } else {
            const array = new Array(8)

            for (let z = 10; z < 18; ++z) {
                array[z - 10] = size / Math.pow(2, z)
            }
            return array
        }
    }

    private getMatrixIds(): string[] {
        if (this.state.imageInfo?.type !== ImageType.WSI) {
            return []
        } else {
            const array = new Array(8)

            for (let z = 10; z < 18; ++z) {
                array[z - 10] = z
            }
            return array
        }
    }

    private getProjection(extent: Extent): Projection {
        return new Projection({
            code: 'wsi-image',
            units: 'metric',
            extent: extent,
            metersPerUnit: 1,
            worldExtent: extent,
            getPointResolution: (pixelRes, point) => {
                const spacing = this.state.imageInfo.scale / 10 ** 3
                return pixelRes * spacing
            }
        })
    }

    private getImageSourceStatic(extent: Extent, projection: Projection, resolutions: number[], matrixIds: string[]): Static {
        return new Static({
            attributions: ATTRIBUTIONS,
            url: `${this.state.imageInfo?.url}&brightness=${this.state.selectedBrightness}&contrast=${this.state.selectedContrast}&convolve=${this.state.selectedKernel}`,
            projection: projection,
            imageExtent: extent,
            crossOrigin: 'anonymous'
        })
    }

    private getImageSourceWMTS(extent: Extent, projection: Projection, resolutions: number[], matrixIds: string[]): WMTS {
        return new WMTS({
            attributions: ATTRIBUTIONS,
            url: `${this.state.imageInfo?.url}?brightness=${this.state.selectedBrightness}&contrast=${this.state.selectedContrast}&convolve=${this.state.selectedKernel}`,
            layer: 'base',
            style: 'default',
            matrixSet: 'wsi-image',
            format: 'image/jpeg',
            projection: projection,
            tileGrid: new WMTSTileGrid({
                origin: getTopLeft(extent),
                resolutions: resolutions,
                matrixIds: matrixIds
            }),
            wrapX: false,
            transition: 10,
            crossOrigin: 'anonymous'
        })
    }

    private getImageLayerWMTS(extent: Extent, projection: Projection, resolutions: number[], matrixIds: string[]): TileLayer<WMTS> {
        return new TileLayer({
            opacity: 1.0,
            source: this.getImageSourceWMTS(extent, projection, resolutions, matrixIds)
        })
    }

    private getImageLayerStatic(extent: Extent, projection: Projection, resolutions: number[], matrixIds: string[]): Layer<any> {
        return new ImageLayer({
            source: this.getImageSourceStatic(extent, projection, resolutions, matrixIds)
        })
    }

    private getImageLayer(extent: Extent, projection: Projection, resolutions: number[], matrixIds: string[]): Layer<any> {
        if (this.state.imageInfo?.type !== ImageType.WSI) {
            return this.getImageLayerStatic(extent, projection, resolutions, matrixIds)
        } else {
            return this.getImageLayerWMTS(extent, projection, resolutions, matrixIds)
        }
    }

    private getView(extent: Extent, projection: Projection): View {
        return new View({
            projection: projection,
            center: getCenter(extent),
            zoom: 2,
            maxZoom: 10
        })
    }

    @Emit('changes-made')
    public notifyChangesMade():void {}

    @Emit('selectedColorChange')
    public selectColor(color: string) {
        this.state.selectColor = color
    }

    @Emit('changeTool')
    private emitChangeTool(type: ToolType) { }

    public changeTool(type: ToolType): void {
        if (!this.state.map) {
            // this case is a particular case when the map is not loaded yet but an external actor (toolbox) ask to set the tool.
            // we prepare the selected tool to be changed when the map is ready
            this.state.selectedTool = type
            return
        }

        if (this.state.selectedTool) {
            const oldTool = ToolFactory.getInstance().getCachedTool(this.state.selectedTool)
            oldTool?.removeInteractions()
        }

        const tool = ToolFactory.getInstance().getTool(type, this.state, this, this)
        tool?.removeInteractions()

        try {
            tool?.addInteractions()
        } catch (error) {
            this.$_showErrorModal(this.$tc((error as any).message) as string)
        }
        this.state.selectedTool = type
        this.emitChangeTool(type)
    }

    private async loadFeatures():Promise<void> {
        const features = await this.featuresManager?.loadFeatures()
        if (features && features[0]) {
            this.state.annotationId = features[0].annotationId
            this.state.annotations = (new GeoJSON()).readFeatures(features[0].annotations)
        } else {
            this.state.annotationId = ''
            this.state.annotations = undefined
        }
    }

    private getAnnotationSourceFromJsonFeatures(geojsonFeatures: Feature<Geometry>[]): VectorSource<Geometry> {
        if (geojsonFeatures) {
            geojsonFeatures.forEach(feature => {
                this.applyAnnotationDetails(feature, false, false, false)
            })
        }

        return new VectorSource({
            features: geojsonFeatures
        })
    }

    private generateAnnotationLayer(vectorSource: VectorSource<Geometry>): VectorLayer<VectorSource<Geometry>> {
        return new VectorLayer <VectorSource<Geometry>>({
            source: vectorSource
        })
    }

    private generateTooltipLayer(): VectorLayer<VectorSource<Geometry>> {
        return new VectorLayer<VectorSource<Geometry>>({
            source: new VectorSource<Geometry>()
        })
    }

    public recalculateAllAreasAndDistances(recreateTooltips: boolean = false): void {
        this.state.vectorLayer?.getSource().getFeatures().forEach((feature:Feature<Geometry>) => this.recalculateAreaAndDistance(feature, recreateTooltips))
    }

    public recalculateAreaAndDistance(feature: Feature<Geometry>, recreateTooltips: boolean = false): void {
        const type = feature.get('type') ? feature.get('type') : this.state.selectedTool
        if (type === ToolType.FREE_POLYGON || type === ToolType.CIRCLE || type === ToolType.SQUARE) {
            this.recalculateArea(feature, recreateTooltips)
        } else if (type === ToolType.MEASURING) {
            this.recalculateDistance(feature as Feature<LineString>, recreateTooltips)
        }
    }

    public recalculateDistance(feature: Feature<LineString>, recreateTooltips: boolean = false): void {
        const type = feature.get('type') ? feature.get('type') : this.state.selectedTool
        const distance = feature.get('title')
        let newDistance = distance
        if (type === ToolType.MEASURING) {
            newDistance = this.calculateDistanceStr(feature)
        }
        if (distance !== newDistance) {
            feature.set('title', newDistance)
            if (recreateTooltips) {
                this.removeToolTip(feature)
                let showTitle = feature.get('showTitle')
                if (showTitle) {
                    this.createFixedToolTip(feature)
                }
            }
        }
    }

    public recalculateArea(feature: Feature<Geometry>, recreateTooltips:boolean = false): void {
        const type = feature.get('type') ? feature.get('type') : this.state.selectedTool
        const area = feature.get('area')
        let newArea = area
        if (type === ToolType.FREE_POLYGON || type === ToolType.CIRCLE || type === ToolType.SQUARE) {
            newArea = this.calculateAreaStr(<Feature<Polygon>>feature)
        }
        if (newArea !== area) {
            feature.set('area', newArea)
            if (recreateTooltips) {
                this.removeToolTip(feature)
                let showArea = feature.get('showArea')
                if (showArea) {
                    this.createFixedToolTip(feature)
                }
            }
        }
    }

    public async applyAnnotationDetails(feature: Feature<Geometry>, showModal: boolean, edit: boolean = false, notifyChanges:boolean = true):Promise<void> {
        const type = feature.get('type') ? feature.get('type') : this.state.selectedTool
        const id = feature.get('id') ? feature.get('id') : new Date().getTime()
        let title = feature.get('title')
        let text = feature.get('text')
        let color = feature.get('color') ? feature.get('color') : this.state.selectColor
        let showTitle = feature.get('showTitle')
        let showArea = feature.get('showArea')
        this.recalculateArea(feature)
        let area = feature.get('area')

        if (type === ToolType.MEASURING) {
            title = this.calculateDistanceStr(<Feature<LineString>>feature)
            showTitle = true
            showArea = false
        } else if (showModal) {
            await new Promise((resolve, reject) => {
                const featureColor = feature.get('color') ? feature.get('color') : this.state.userConfiguration?.defaultColor
                this.modalViewerWrapper.show([title, text, showArea, showTitle, featureColor, area, type, () => {
                    const modalViewerObj = this.modalViewerWrapper.$children[0]
                    title = modalViewerObj.title
                    text = modalViewerObj.text
                    if (text) {
                        text = this.stringDivider(text, 16, '\n')
                    }
                    color = modalViewerObj.selectedColor
                    showTitle = modalViewerObj.showTitle
                    showArea = modalViewerObj.showArea
                    modalViewerObj.hide()
                    resolve(true)
                }, () => {
                    this.modalViewerWrapper.hide()
                    if (!edit && (this.state.selectedTool === ToolType.FREE_POLYGON ||
                        this.state.selectedTool === ToolType.CIRCLE ||
                        this.state.selectedTool === ToolType.SQUARE)) {
                        this.state.vectorSource?.removeFeature(feature)
                    }
                    reject(new Error('drawing canceled'))
                }])
            }).catch(() => {
            })
        }

        feature.set('type', type)
        feature.set('title', title)
        feature.set('text', text)
        feature.set('color', color)
        feature.set('showTitle', showTitle)
        feature.set('showArea', showArea)
        feature.set('id', id)
        feature.setStyle(this.getAnnotationStyle)

        this.removeToolTip(feature)

        if (showTitle || showArea) {
            this.createFixedToolTip(feature)
        }

        if (notifyChanges) { this.notifyChangesMade() }
    }

    getTooltipStyle(feature: FeatureLike):Style {
        return new Style({
            renderer: (pixelCoordinates: Coordinate | Coordinate[] | Coordinate[][], state: State) => {
                let context = state.context
                let geometry: SimpleGeometry = state.geometry.clone() as SimpleGeometry
                geometry.setCoordinates(pixelCoordinates)

                context.save()

                let closestPoint = geometry.getClosestPoint([0, 0])
                const font = '15px Verdana'
                context.font = font
                const padding = 5
                const text = feature.get('label')
                const metrics = context.measureText(text)
                context.fillStyle = `rgba(255, 204, 51, 0.8)`
                context.strokeStyle = 'rgba(255,255,255,1)'
                const textHeight = (metrics.actualBoundingBoxAscent)
                const recWidth = metrics.width + padding * 2
                const recWidthHalf = recWidth / 2
                const recHeight = textHeight + padding * 2
                const triangleHeight = recHeight * 0.5
                const recX = closestPoint[0] - recWidthHalf
                const recY = closestPoint[1] - recHeight - triangleHeight

                this.roundRect(context, recX, recY, recWidth, recHeight, 5, true, true)
                context.restore()

                context.font = font
                context.fillStyle = 'rgba(0,0,0,1)'
                context.fillText(text, recX + padding, recY + padding + textHeight)

                context.restore()
            }
        })
    }

    getAnnotationStyle(feature: FeatureLike):Style {
        let strokeColor
        let fillColor
        const type = feature.get('type')
        const color = feature.get('color')

        if (type === ToolType.MEASURING) {
            strokeColor = color
            fillColor = 'rgba(255, 255, 255, 0)'
        } else if (type === ToolType.TEXT) {
            strokeColor = 'rgba(255, 255, 255, 0)'
            fillColor = 'rgba(255, 255, 255, 0)'
        } else {
            strokeColor = color
            fillColor = 'rgba(255, 255, 255, 0.1)'
        }

        return new Style({
            stroke: new Stroke({
                color: strokeColor,
                width: 2
            }),
            fill: new Fill({
                color: fillColor
            })
        })
    }

    private roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, rad:number = 5, fill:boolean = true, stroke:boolean = true):void {
        const radius = { tl: rad, tr: rad, br: rad, bl: rad }
        ctx.beginPath()
        ctx.moveTo(x + radius.tl, y)
        ctx.lineTo(x + width - radius.tr, y)
        ctx.quadraticCurveTo(x + width, y, x + width, y + radius.tr)
        ctx.lineTo(x + width, y + height - radius.br)
        ctx.quadraticCurveTo(x + width, y + height, x + width - radius.br, y + height)

        const halfWidth = width / 2
        const triangleWidth = height
        const triangleWidthHalf = triangleWidth / 2
        const triangleHeight = height * 0.5
        ctx.lineTo(x + halfWidth + triangleWidthHalf, y + height)
        ctx.lineTo(x + halfWidth, y + height + triangleHeight)
        ctx.lineTo(x + halfWidth - triangleWidthHalf, y + height)

        ctx.lineTo(x + radius.bl, y + height)

        ctx.quadraticCurveTo(x, y + height, x, y + height - radius.bl)
        ctx.lineTo(x, y + radius.tl)
        ctx.quadraticCurveTo(x, y, x + radius.tl, y)
        ctx.closePath()
        if (fill) {
            ctx.fill()
        }
        if (stroke) {
            ctx.stroke()
        }
    }

    public calculateArea(polygonFeature: Feature<Polygon>): number {
        const measureScale = this.state.imageInfo.scale

        const polygon = polygonFeature.getGeometry()
        if (polygon && polygon.getArea) {
            const area: number = polygon.getArea()
            return area * measureScale * measureScale
        }
        return 0
    }

    public calculateAreaStr(polygonFeature: Feature<Polygon>): string | undefined {
        if (!this.state.imageInfo.scale || this.state.imageInfo.scale <= 0) {
            return undefined
        }
        let area = this.calculateArea(polygonFeature)
        let normalizedArea = this.normalizeDistance(area, true)
        return normalizedArea.scale.toFixed(2) + normalizedArea.unit + '²'
    }

    private calculateDistance(lineFeature: Feature<LineString>): number {
        const measureScale: number = this.state.imageInfo.scale

        const lineString = lineFeature.getGeometry()
        if (lineString) {
            const geometryLenght: number = lineString.getLength()
            return geometryLenght * measureScale
        }
        return 0
    }

    private calculateDistanceStr(lineFeature: Feature<LineString>):string {
        let distance = this.calculateDistance(lineFeature)
        let normalizedDistance = this.normalizeDistance(distance)
        return normalizedDistance.scale.toFixed(2) + normalizedDistance.unit
    }

    getFeatureAtPosition(position: Pixel):Nullable<FeatureLike> {
        return this.state.map?.forEachFeatureAtPixel(position, ft => {
            const type = ft.getGeometry()?.getType()
            return (type === 'Polygon' || type === 'LineString') ? ft : null
        }, { hitTolerance: 10 })
    }

    /**
     * Handle pointer move.
     * @param {import("../src/ol/MapBrowserEvent").default} evt The event.
     */
    private pointerMoveHandler(evt: MapBrowserEvent<MouseEvent>):void {
        if (this.state.map) {
            const position = this.state.map.getEventPixel(evt.originalEvent)
            const featureAtPosition = this.getFeatureAtPosition(position)

            if (!featureAtPosition || this.state.selectedTool) {
                this.state.helpTooltip?.setPosition(undefined)
            } else {
                if (evt.dragging) {
                    return
                }

                const title = featureAtPosition.get('title')
                const text = featureAtPosition.get('text')
                const area = featureAtPosition.get('area')

                if (!title && !text && !area) {
                    this.state.helpTooltip?.setPosition(undefined)
                    return
                }

                let helpMsg = ''

                if (title) {
                    helpMsg += '<b>' + title + '</b><br/>'
                }

                if (text) {
                    helpMsg += this.stringDivider(text, 20, '<br/>') + '<br/>'
                }

                if (area) {
                    helpMsg += area
                }

                if (this.state.helpTooltipElement) {
                    this.state.helpTooltipElement.innerHTML = helpMsg
                    this.state.helpTooltip?.setPosition(evt.coordinate)
                    this.state.helpTooltipElement.classList.remove('hidden')
                }
            }
        }
    }

    private stringDivider(str: string, width: number, spaceReplacer: string): string {
        if (str.length > width) {
            let p = width
            while (p > 0 && (str[p] !== ' ' && str[p] !== '-')) {
                p--
            }
            if (p > 0) {
                let left
                if (str.substring(p, p + 1) === '-') {
                    left = str.substring(0, p + 1)
                } else {
                    left = str.substring(0, p)
                }
                const right = str.substring(p + 1)
                return left + spaceReplacer + this.stringDivider(right, width, spaceReplacer)
            }
        }
        return str
    }

    private pointerMoveOutHandler() {
        this.state.helpTooltipElement?.classList.add('hidden')
        this.state.helpTooltip?.setPosition(undefined)
    }

    private getFeatureContextMenu(feature: Feature<Geometry>): (ContextMenuItem|string)[] {
        const contextMenuOptions: (ContextMenuItem|string)[] = [this.getCloseContextMenuItem(), '-']

        if (feature.getGeometry()?.getType() === 'Polygon') {
            contextMenuOptions.push(
                {
                    text: '<i class="fal fa-edit mr-2"></i> ' + this.$t('viewer.tooltips.editDetails'),
                    callback: () => {
                        this.closeContextMenu()
                        this.applyAnnotationDetails(feature, true, true)
                    }
                })
        }

        contextMenuOptions.push(
            {
                text: '<i class="fal fa-align-slash mr-2"></i> ' + this.$t('viewer.contextMenu.remove'),
                callback: () => {
                    this.closeContextMenu()
                    this.removeAnnotation(feature)
                }
            }
        )

        return contextMenuOptions
    }

    private closeContextMenu() {
        this.state.contextMenu.pop()
    }

    private getCloseContextMenuItem(): ContextMenuItem {
        return {
            text: '<i class="fal fa-window-close mr-2"></i> ' + this.$t('viewer.contextMenu.close'),
            classname: 'bold',
            callback: this.closeContextMenu
        }
    }

    private removeAnnotation(feature: Feature<Geometry>) {
        this.removeToolTip(feature)
        this.state.vectorSource?.removeFeature(feature)
    }

    private getMapContextMenu():(ContextMenuItem|string)[] {
        return [
            this.getCloseContextMenuItem(),
            '-', // this is a separator
            {
                text: '<i class="fal fa-hand-paper mr-2"></i> ' + this.$t('viewer.tooltips.moveCanvas'),
                classname: 'bold',
                callback: () => {
                    this.closeContextMenu()
                    this.changeTool(ToolType.MOVE_CANVAS)
                }
            },
            {
                text: '<i class="fal fa-tools mr-2"></i> ' + this.$t('viewer.contextMenu.actions'),
                items: [
                    {
                        text: '<i class="fal fa-ruler-triangle mr-2"></i> ' + this.$t('viewer.tooltips.measuring'),
                        callback: () => {
                            this.closeContextMenu()
                            this.changeTool(ToolType.MEASURING)
                        }
                    },
                    {
                        text: '<i class="fal fa-draw-polygon mr-2"></i> ' + this.$t('viewer.tooltips.freePolygon'),
                        callback: () => {
                            this.closeContextMenu()
                            this.changeTool(ToolType.FREE_POLYGON)
                        }
                    },
                    {
                        text: '<i class="fal fa-circle mr-2"></i> ' + this.$t('viewer.tooltips.circle'),
                        callback: () => {
                            this.closeContextMenu()
                            this.changeTool(ToolType.CIRCLE)
                        }
                    },
                    {
                        text: '<i class="fal fa-square mr-2"></i> ' + this.$t('viewer.tooltips.square'),
                        callback: () => {
                            this.closeContextMenu()
                            this.changeTool(ToolType.SQUARE)
                        }
                    },
                    {
                        text: '<i class="fal fa-pencil mr-2"></i> ' + this.$t('viewer.tooltips.edit'),
                        callback: () => {
                            this.closeContextMenu()
                            this.changeTool(ToolType.EDIT)
                        }
                    },
                    {
                        text: '<i class="fal fa-arrows mr-2"></i> ' + this.$t('viewer.tooltips.move'),
                        callback: () => {
                            this.closeContextMenu()
                            this.changeTool(ToolType.MOVE)
                        }
                    }
                ]
            },
            {
                text: '<i class="fal fa-trash mr-2"></i> ' + this.$t('viewer.contextMenu.removeAll'),
                callback: () => {
                    this.closeContextMenu()
                    this.removeAllAnnotations()
                }
            },
            '-', // this is a separator,
            {
                text: '<i class="fal fa-save mr-2"></i> ' + this.$t('global.save'),
                callback: this.requestSave
            }
        ]
    }

    private removeAllAnnotations():void {
        this.state.vectorSource?.getFeatures().forEach((feature) => {
            this.removeAnnotation(feature)
        })
    }

    public createFixedToolTip(feature: Feature<Geometry>):void {
        const fixedToolTipText = this.getFixedToolTipText(feature)
        if (fixedToolTipText) {
            feature.set('label', fixedToolTipText)
        }

        const clonedFeature = feature.clone()
        clonedFeature.setStyle(this.getTooltipStyle(clonedFeature))
        this.state.tooltipLayer?.getSource().addFeature(clonedFeature)
    }

    public getFixedToolTipText(feature: Feature<Geometry>): string {
        let text = ''
        const title = feature.get('title')
        const showTitle = feature.get('showTitle')
        const showArea = feature.get('showArea')
        const area = feature.get('area')

        if (showTitle && title) {
            text = title
        }

        if (showArea && area) {
            text += ' (' + area + ')'
        }

        return text
    }

    public abortAction(): void {
        if (this.state.selectedTool) {
            const oldTool = ToolFactory.getInstance().getCachedTool(this.state.selectedTool)
            if (oldTool) {
                oldTool.removeInteractions()
                this.changeTool(oldTool.type)
            }
        }
    }

    public removeToolTip(feature: FeatureLike):void {
        const id = feature.get('id')
        const tooltipFeature = this.state.tooltipLayer?.getSource().getFeatures().find((feature:Feature<Geometry>) => feature.get('id') === id)
        if (tooltipFeature) { this.state.tooltipLayer?.getSource().removeFeature(tooltipFeature) }
    }

    public async requestSave():Promise<void> {
        this.modalAttachmentFilename.show([this.state.imageInfo.filename, this.state.annotationId])
    }

    private async takeMapSnapshot(): Promise<string> {
        return new Promise((resolve) => {
            this.state.map?.once('rendercomplete', (event) => {
                const mapCanvas = document.createElement('canvas')
                const size = this.state.map?.getSize()
                if (size) {
                    mapCanvas.width = size[0]
                    mapCanvas.height = size[1]
                    const mapContext = mapCanvas.getContext('2d')
                    Array.prototype.forEach.call(document.querySelectorAll('.ol-layer canvas'), (canvas) => {
                        if (canvas.width > 0) {
                            const opacity = canvas.parentNode.style.opacity
                            if (mapContext !== null) {
                                mapContext.globalAlpha = opacity === '' ? 1 : Number(opacity)
                                const transform = canvas.style.transform
                                // Get the transform parameters from the style's transform matrix
                                const matrix = transform.match(/^matrix\(([^(]*)\)$/)[1].split(',').map(Number)
                                // Apply the transform to the export map context
                                CanvasRenderingContext2D.prototype.setTransform.apply(mapContext, matrix)
                                mapContext.drawImage(canvas, 0, 0)
                            }
                        }
                    })

                    resolve(mapCanvas.toDataURL('image/png'))
                }
            })
            this.state.map?.renderSync()
        })
    }

    public async save(attachmentFilename: string) {
        if (this.state.map) {
            let map = this.state.map
            let view = map.getView()
            let extent = this.state.imageExtent
            const elcanvas = document.querySelector('.ol-layer canvas') as HTMLElement
            const elmap = document.querySelector('#map.map') as HTMLElement
            this.state.imageLayer?.setVisible(false) // only capturing vectors
            if (elcanvas && elmap) {
                // resizing the map to 1:1
                const oldCanvasStyle = elcanvas.getAttribute('style') || ''
                const oldMapStyle = elmap.getAttribute('style') || ''
                elcanvas.setAttribute('style', `width:${this.state.imageInfo.width}px !important;height:${this.state.imageInfo.height}px !important`)
                elmap.setAttribute('style', `${oldMapStyle};width:${this.state.imageInfo.width}px !important;height:${this.state.imageInfo.height}px !important`)
                const oldSize = map?.getSize()
                const oldRotation = view.getRotation()
                const oldZoom = view.getZoom()
                const oldCenter = view.getCenter()
                map?.setSize([this.state.imageInfo.width, this.state.imageInfo.height])
                view.setRotation(0)
                view?.setViewportSize([this.state.imageInfo.width, this.state.imageInfo.height])
                if (extent) {
                    view?.fit(extent, <any>{ size: map?.getSize() })
                }
                // removing tool to avoid cursor tool capture
                if (this.state.selectedTool) {
                    const oldTool = ToolFactory.getInstance().getCachedTool(this.state.selectedTool)
                    oldTool?.removeInteractions()
                }

                // capture
                const snapshotVectorBytes = await this.takeMapSnapshot()
                this.state.imageLayer?.setVisible(true)
                const snapshotCompleteBytes = await this.takeMapSnapshot()
                // console.log(snapshotVectorBytes)

                // restoring old states
                elmap.setAttribute('style', oldMapStyle)
                elcanvas.setAttribute('style', oldCanvasStyle)
                map?.setSize(oldSize)
                view.setRotation(oldRotation)
                view?.setViewportSize(oldSize)
                if (oldZoom) { view.setZoom(oldZoom) }
                if (oldCenter) { view.setCenter(oldCenter) }
                if (this.state.selectedTool) {
                    this.changeTool(this.state.selectedTool)
                }

                // saving the snapshot
                const thumbnailBytes: string = await SampleAttachmentService.thumbnailify(snapshotCompleteBytes) as any
                const writer = new GeoJSON()
                const geojsonFeatures = writer.writeFeatures(this.state.vectorSource?.getFeatures() as Feature<Geometry>[])

                const annotationIdToUse = this.state.imageInfo.filename !== attachmentFilename ? '' : this.state.annotationId
                const newAnnoationId = await this.featuresManager?.saveFeatures(annotationIdToUse, attachmentFilename, geojsonFeatures, snapshotVectorBytes, thumbnailBytes)
                if (newAnnoationId) { this.state.annotationId = newAnnoationId }
                this.state.imageInfo.filename = attachmentFilename
            }
        }
    }

    public zoomIn() {
        const currentZoom = this.state.view!.getZoom()
        if (currentZoom) { this.state.view!.setZoom(currentZoom + 1) }
    }

    public zoomOut() {
        const currentZoom = this.state.view!.getZoom()
        if (currentZoom) { this.state.view!.setZoom(currentZoom - 1) }
    }

    public originalZoom(): void {
        if (this.state.imageExtent) {
            this.state.view!.fit(this.state.imageExtent)
            this.state.zoomLabel = '0.00'
        }
    }

    public panMap(direction: PanDirection) {
        let newCenterInPx
        const center = this.state.view!.getCenter()
        if (center) {
            const centerInPx = this.state.map!.getPixelFromCoordinate(center)

            switch (direction) {
            case PanDirection.RIGHT:
                newCenterInPx = [centerInPx[0] - 100, centerInPx[1]]
                break
            case PanDirection.LEFT:
                newCenterInPx = [centerInPx[0] + 100, centerInPx[1]]
                break
            case PanDirection.BOTTOM:
                newCenterInPx = [centerInPx[0], centerInPx[1] - 100]
                break
            case PanDirection.TOP:
                newCenterInPx = [centerInPx[0], centerInPx[1] + 100]
                break
            }
            const newCenter = this.state.map!.getCoordinateFromPixel(newCenterInPx)
            this.state.view!.setCenter(newCenter)
        }
    }

    public showPictureInfo() {
        let info = new Map<string, string>()
        info.set('filename', this.imageInfo.filename)
        info.set('width', `${this.imageInfo.width} px`)
        info.set('height', `${this.imageInfo.height} px`)
        info.set('length', this.formatBytes(this.state.imageInfo.length, 2))
        if (this.imageInfo.scale) { info.set('scale', `${this.imageInfo.scale.toFixed(2)} mm`) }
        info.set('type', `${this.imageInfo.mimetype}`)

        let mapInfo
        if (this.state.imageInfo?.type !== ImageType.WSI) {
            mapInfo = new Map([...info])
        } else {
            mapInfo = new Map([...info, ...this.wmtsImageInfo.headers])
        }
        this.modalPictureInfo.show(mapInfo)
    }

    private formatBytes(bytes: number, decimals: number = 2): string {
        const units = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
        if (bytes === 0) return '0 Bytes'
        const index = Math.floor(Math.log(bytes) / Math.log(1024))
        return parseFloat((bytes / Math.pow(1024, index)).toFixed(decimals)) + ' ' + units[index]
    }

    public showConfiguration(): void {
        this.modalConfigurationViewer.show()
    }

    protected setZoomFactor(zoomFactor: string) {
        if (this.state.imageInfo?.type !== ImageType.WSI) {
            this.state.map?.getView().setResolution(1 / parseInt(zoomFactor))
        } else {
            this.state.map?.getView().setResolution(this.state.wmtsImageInfo.objective / parseInt(zoomFactor))
        }
    }

    public toogleMap(): void {
        this.state.showMap = !this.state.showMap
        this.emitOnShowMap(this.state.showMap)
    }

    public toogleFx():void {
        this.state.showFx = !this.state.showFx
        this.emitOnShowFx(this.state.showFx)
    }

    @Emit('showMapEvent')
    private emitOnShowMap(showMap: boolean): void { }

    @Emit('showFxEvent')
    private emitOnShowFx(showFx: boolean): void { }

    changePosition(event: any): void {
        const thumbnailWidth = event.target.clientWidth
        const thumbnailHeight = event.target.clientHeight

        if (this.state.imageExtent) {
            const imageWidth = this.state.imageExtent[2]
            const imageHeight = this.state.imageInfo.type !== ImageType.WSI ? this.state.imageExtent[3] : this.state.imageExtent[1]

            const widthRatio = imageWidth / thumbnailWidth
            const heightRatio = imageHeight / thumbnailHeight

            const rect = event.target.getBoundingClientRect()
            const x = event.clientX - rect.left
            const y = event.clientY - rect.top

            if (this.state.imageInfo.type !== ImageType.WSI) {
                this.state.view?.setCenter([x * widthRatio, (imageHeight - y * heightRatio)])
            } else {
                this.state.view?.setCenter([x * widthRatio, y * heightRatio])
            }
        }
    }

    public configurationChanged(event: any[]): void {
        for (let i = 0; i < event.length; i = i + 2) {
            let configKey = event[i]
            let configValue = event[i + 1]
            switch (configKey) {
            case 'showAnnotations':
                if (configValue !== undefined) {
                    this.showAnnotations(configValue)
                }
                break
            case 'defaultColor':
                this.state.userConfiguration.defaultColor = configValue
                break
            case 'showTitle':
                this.state.userConfiguration.showTitle = configValue
                break
            case 'showScale':
                this.state.userConfiguration.showScale = configValue
                break
            case 'showAnnotationWindow':
                this.state.userConfiguration.showAnnotationWindow = configValue
                break
            }
        }
        this.configurationManager?.saveConfiguration(this.state.userConfiguration)
    }

    _resetFx(): void {
        this.state.selectedBrightness = 0
        this.state.selectedContrast = 1
        this.state.selectedKernel = KernelType.NONE
        this.updateImageLayer()
    }

    public showAnnotations(visible?: boolean): void {
        if (visible !== undefined) {
            this.state.userConfiguration.showAnnotations = visible
        }
        this.state.vectorLayer!.setVisible(this.state.userConfiguration.showAnnotations)

        let features = this.state.vectorSource?.getFeatures()
        features?.forEach(feature => {
            let id = feature.get('id')
            let overlay = this.state.map?.getOverlayById(id)
            if (!this.state.userConfiguration.showAnnotations) {
                overlay?.getElement()?.classList.add('hidden')
            } else {
                overlay?.getElement()?.classList.remove('hidden')
            }
        })
    }

    private normalizeDistance(distanceInMillimeters: number, area = false): ScaleInfo {
        let scaleInfo: ScaleInfo = {
            scale: distanceInMillimeters,
            unit: UnitType.MM
        }

        if (distanceInMillimeters > 10) {
            const units = [UnitType.MM, UnitType.CM, UnitType.DM, UnitType.M]
            let unitIndex = 0
            while (scaleInfo.scale > 10 && unitIndex < units.length - 1) {
                unitIndex++
                scaleInfo.unit = units[unitIndex]
                scaleInfo.scale = scaleInfo.scale / (10 * (area ? 10 : 1))
            }
        } else if (distanceInMillimeters < 1) {
            const units = [UnitType.MM, UnitType.MICRAS, UnitType.NM]
            let unitIndex = 0
            while (scaleInfo.scale < 1 && unitIndex < units.length - 1) {
                unitIndex++
                scaleInfo.unit = units[unitIndex]
                scaleInfo.scale = scaleInfo.scale * 10 * (area ? 10 : 1)
            }
        }

        return scaleInfo
    }

    public askScale(lineFeature: Feature<LineString>): Promise<ScaleInfo> {
        return new Promise((resolve, reject) => {
            const currentScale = this.calculateDistance(lineFeature)
            const normalizedScale = this.normalizeDistance(currentScale)

            if (!this.state.imageInfo.scale || this.state.imageInfo.scale <= 0) {
                normalizedScale.unit = UnitType.MICRAS
                if (this.state.imageInfo.type === ImageType.MACRO) {
                    normalizedScale.unit = UnitType.MM
                }
            }

            this.modalMeasureScaleWrapper.show([normalizedScale.unit, normalizedScale.scale.toFixed(2), () => {
                const modalViewerObj = this.modalMeasureScaleWrapper.$children[0]
                const scaleInfo: ScaleInfo = {
                    scale: modalViewerObj.scale,
                    unit: modalViewerObj.unit
                }
                modalViewerObj.hide()
                resolve(scaleInfo)
            }, () => {
                this.modalMeasureScaleWrapper.hide()
                reject(new Error('Scale canceled'))
            }])
        })
    }

    public saveCurrentMeasureScale(): Promise<void> {
        return this.featuresManager!.saveCurrentMeasureScale()
    }
}
